import { type FC, memo, useMemo, useState } from "react";

import Button from "@/design-system/button";
import type { IFlyerTabResources } from "@/src/flyers/FlyersTabResources.Resources";
import useAjax, { type IAjax } from "@Components/Hooks/useAjax";
import Text from "@Components/Text";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMaxToSmMin, type QueryXsMax, colors } from "../../styles/tokens.stylex";
import AlphabetList, { defaultAlphabetFilter } from "../common/AlphabetList";
import type { IFlyersTabBrandsItem } from "./FlyersTabBrandsItem";
import FlyersTabBrandsItem from "./FlyersTabBrandsItem";

export interface IFlyersTabBrands extends IAjax<IFlyersTabBrandsItem[]> {
    itemsToShow: number;
    resources: IFlyerTabResources["brands"];
}

const FlyersTabBrands: FC<IFlyersTabBrands> = memo(
    (props) => {
        const [selectedFilter, setSelectedFilter] = useState<string>(defaultAlphabetFilter);
        const [rowsToShow, setRowsToShow] = useState<number>(1);

        const itemsToShow = props.itemsToShow * rowsToShow;

        const { data } = useAjax({
            initialData: props.initialData || [],
        });

        const anchorClick = (anchor: string) => {
            if (!anchor) {
                return;
            }
            setRowsToShow(1);
            setSelectedFilter(anchor.toLocaleUpperCase());
        };

        const brandListFiltered = useMemo(() => {
            if (selectedFilter === defaultAlphabetFilter) {
                return data;
            }
            const updatedList = data.filter((item) => item.groupKey.search(selectedFilter) !== -1);

            return updatedList;
        }, [selectedFilter, rowsToShow]);

        const renderFilters = () => {
            const bradsGroupKeys = data.map((item) => item.groupKey);

            return (
                <AlphabetList
                    onAnchorClick={anchorClick}
                    minFullWidthDevice="Desktop"
                    groupKeys={bradsGroupKeys}
                    allBrandsTitle={props.resources.filtersTitle}
                />
            );
        };

        const renderBrands = () => {
            const brandList = brandListFiltered;

            return (
                <div {...stylex.props(styles.brands)}>
                    {brandList.slice(0, itemsToShow).map((item, i) => (
                        <div
                            key={item.brandId + i}
                            id={`brand-${item.groupKey}-${i}`}
                            {...stylex.props(styles.brandItem)}
                        >
                            <FlyersTabBrandsItem {...item} />
                        </div>
                    ))}
                </div>
            );
        };

        const showMoreBrandsBtn = brandListFiltered.length > itemsToShow;

        return (
            <>
                <Text textElement="h2" fontStyle="h4" fontWeight="400" styleX={styles.title}>
                    {props.resources.title}
                </Text>
                {renderFilters()}
                {renderBrands()}
                {showMoreBrandsBtn && (
                    <Button
                        as="button"
                        size="None"
                        variant="transparent"
                        styleX={styles.showMoreBtn}
                        onClick={() => setRowsToShow(rowsToShow + 1)}
                    >
                        {props.resources.showMoreBtn}
                    </Button>
                )}
            </>
        );
    },
    (prev, next) =>
        prev.itemsToShow === next.itemsToShow &&
        prev.resources === next.resources &&
        prev.initialData.length === next.initialData.length,
);

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMinSmMax: QuerySmMaxToSmMin = "@media (max-width: 989px) and (min-width: 760px)";

const styles = stylex.create({
    title: {
        color: colors.grayDark,
        textTransform: "uppercase",
    },

    brands: {
        margin: { default: "0 -10px", [xsMax]: "0" },
        lineHeight: { default: null, [xsMax]: "0" },
    },

    brandItem: {
        padding: { default: "10px", [xsMax]: "0" },
        display: "inline-block",
        position: { default: null, [xsMax]: "relative" },
        width: { default: "20%", [smMinSmMax]: "calc( 100% / 3 )", [xsMax]: "100%" },
        "::after": {
            backgroundColor: { default: null, [xsMax]: "#000" },
            content: { default: null, [xsMax]: "" },
            opacity: { default: null, [xsMax]: 0.1 },
            position: { default: null, [xsMax]: "absolute" },
            bottom: { default: null, [xsMax]: 0 },
            height: { default: null, [xsMax]: 2 },
            left: { default: null, [xsMax]: 0 },
            width: { default: null, [xsMax]: "100%" },
        },
    },

    showMoreBtn: {
        color: colors.fg_Accent,
        marginTop: "20px",
        width: "100%",
    },
});

export default FlyersTabBrands;
